import { useCallback, useMemo } from 'react';
import { createRepeatedTaxRecalculator, useRepeatRowsHandler } from '@/lib/table-handlers';
import { calcUtils, handleUpdateRowFields, transformFormData } from '../../../utils';
import { useInputTableRows } from '../DetailTab/useInputTableRows';
import { formSchema, initialFormValues } from '../../../schema';
import { useFormFieldState } from '../../../hooks';
import type { FormDialogProps } from '../types';

const recalculateTaxAfterRepeat = createRepeatedTaxRecalculator<BanHangRecord>({
  getTaxBase: row => Number(row?.tien_nt2 || 0) - Number(row?.ck_nt || 0)
});

export const useFormDialog = ({ formMode, initialData, onClose, onSubmit }: FormDialogProps) => {
  const formState = useFormFieldState(initialData);
  const { state } = formState;
  const detail = useInputTableRows(initialData?.chi_tiet || [], undefined, handleUpdateRowFields, state);
  const constraints = useMemo(
    () => state.taiKhoan?.constraints ?? state.khachHang?.account_data?.constraints ?? [],
    [state.khachHang?.account_data?.constraints, state.taiKhoan?.constraints]
  );
  const schema = useMemo(() => formSchema(constraints), [constraints]);

  const repeatRows = useRepeatRowsHandler({
    setRows: detail.setRows as BanHangRecord,
    onAfterRepeat: recalculateTaxAfterRepeat
  });

  const totals = useMemo(() => calcUtils(detail.rows), [detail.rows]);

  const submit = useCallback(
    (data: BanHangPayload) => {
      const formattedData = transformFormData(data, state, detail.rows, totals, formMode);
      onSubmit(formattedData);
    },
    [detail.rows, formMode, onSubmit, state, totals]
  );

  return {
    formMode,
    initialData,
    initialValues: initialData || initialFormValues,
    title: formMode === 'add' ? 'Mới' : formMode === 'edit' ? 'Sửa' : 'Xem',
    schema,
    formState,
    onClose,
    submit,
    totals,
    constraints,
    detail: {
      ...detail,
      repeatRows
    }
  };
};

export type FormDialogModel = ReturnType<typeof useFormDialog>;
